// DEV-02 的兩種 QR 內容（plan/provisioning-v0.md §4.6、§5.1；Eddy 2026-09-27）：
//   ① 攝影機鏡頭掃：KWF:1|d=<AID>|s=<SSID>|p=<密碼>|c=<pairing_code>|u=<pair_url>|z=<POSIX TZ>
//   ③ 交給 App：https://<品牌 portal 主機>/pair#v=1&s=<binding_id>&c=<pairing_code>&d=<AID>&a=<api 主機>&u=<pair_url>
// 產出的字串含 Wi-Fi 密碼與配對碼：不得 console.log、不送平台、不存 storage（§4.6）。

export type KwfErrorCode = 'SSID_REQUIRED' | 'SSID_TOO_LONG' | 'PSK_LENGTH' | 'BAD_SESSION';

export class KwfError extends Error {
  readonly code: KwfErrorCode;
  constructor(code: KwfErrorCode) {
    super(code);
    this.name = 'KwfError';
    this.code = code;
  }
}

export interface KwfInput {
  aid: string;
  ssid: string;
  /** 空字串 = 開放網路 */
  psk: string;
  code: string;
  pairUrl: string;
  /** POSIX TZ；空 = 不帶 z（設備維持原時區） */
  tz?: string;
}

const enc = new TextEncoder();
const pct = (s: string) => [...enc.encode(s)].map((b) => '%' + b.toString(16).toUpperCase().padStart(2, '0')).join('');

/**
 * §4.6 跳脫（Eddy 2026-09-27 定案）：值是 UTF-8 原文（中文不 encode），
 * 只有 `%`、`|`、值頭尾的空白 percent-encode；`=` 不用（key 只到第一個 `=`）。解析端 percent-decode、不 trim。
 */
export function kwfEscape(v: string): string {
  const body = v.replace(/%/g, '%25').replace(/\|/g, '%7C');
  const lead = /^\s+/.exec(body)?.[0] ?? '';
  const rest = body.slice(lead.length);
  const trail = /\s+$/.exec(rest)?.[0] ?? '';
  return pct(lead) + rest.slice(0, rest.length - trail.length) + pct(trail);
}

const PAIRING_CODE = /^ot_[A-Za-z0-9_-]{22}$/;

/** Wi-Fi 欄位檢查（開 session 前先擋，免得開了才發現 QR 產不出來）；null = 可以。 */
export function checkWifi(ssid: string, psk: string): KwfErrorCode | null {
  if (!ssid) return 'SSID_REQUIRED';
  if (enc.encode(ssid).length > 32) return 'SSID_TOO_LONG';
  if (psk && !(/^[0-9a-fA-F]{64}$/.test(psk) || (psk.length >= 8 && psk.length <= 63))) return 'PSK_LENGTH';
  return null;
}

/** QR byte mode 要 UTF-8：qrcode-generator 預設每個字元取低 8 bit，先轉成「一個字元一個 byte」的字串。 */
export function utf8Binary(s: string): string {
  let out = '';
  for (const b of enc.encode(s)) out += String.fromCharCode(b);
  return out;
}

export function buildKwf(i: KwfInput): string {
  const bad = checkWifi(i.ssid, i.psk);
  if (bad) throw new KwfError(bad);
  if (!i.aid || !PAIRING_CODE.test(i.code) || !/^https:\/\//.test(i.pairUrl)) throw new KwfError('BAD_SESSION');
  const f: [string, string][] = [['d', i.aid], ['s', i.ssid]];
  if (i.psk) f.push(['p', i.psk]);
  f.push(['c', i.code], ['u', i.pairUrl]);
  if (i.tz) f.push(['z', i.tz]);
  return 'KWF:1|' + f.map(([k, v]) => `${k}=${kwfEscape(v)}`).join('|');
}

/** 設備端解析（韌體 QR plugin / emulator 同一套規則）；這裡只給測試做來回驗證。不認得的版本回 null。 */
export function parseKwf(s: string): Record<string, string> | null {
  const parts = s.split('|');
  if (parts.shift() !== 'KWF:1') return null;
  const out: Record<string, string> = {};
  for (const p of parts) {
    const eq = p.indexOf('=');
    if (eq <= 0) throw new Error('INVALID_FIELD');
    const k = p.slice(0, eq);
    if (!['d', 's', 'p', 'c', 'u', 'z'].includes(k)) continue;
    if (k in out) throw new Error('DUPLICATE_FIELD');
    out[k] = decodeURIComponent(p.slice(eq + 1));
  }
  return out;
}

export interface PairLinkInput {
  portalHost: string;
  bindingId: string;
  code: string;
  aid: string;
  /** 家戶的 plane api（endpoints.api，完整網址或主機名）；連結只放主機名 */
  api: string;
  pairUrl: string;
}

export function apiHostOf(api: string): string {
  try {
    return new URL(api.includes('://') ? api : `https://${api}`).host;
  } catch {
    return '';
  }
}

/** §5.1：參數放 fragment（不送伺服器、不進 access log）。 */
export function buildPairLink(i: PairLinkInput): string {
  const q: [string, string][] = [
    ['v', '1'],
    ['s', i.bindingId],
    ['c', i.code],
    ['d', i.aid],
    ['a', apiHostOf(i.api)],
    ['u', i.pairUrl],
  ];
  return `https://${i.portalHost}/pair#` + q.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
}

// ---- 時區：IANA → POSIX TZ（z 欄位）----
// 有 DST 的時區查表（同 app/home-base web 的 timezones.ts 起始集）；沒 DST 的用固定偏移算；都不行就不帶 z。
const POSIX: Record<string, string> = {
  'America/Anchorage': 'AKST9AKDT,M3.2.0,M11.1.0',
  'America/Los_Angeles': 'PST8PDT,M3.2.0,M11.1.0',
  'America/Denver': 'MST7MDT,M3.2.0,M11.1.0',
  'America/Chicago': 'CST6CDT,M3.2.0,M11.1.0',
  'America/New_York': 'EST5EDT,M3.2.0,M11.1.0',
  'America/Toronto': 'EST5EDT,M3.2.0,M11.1.0',
  'America/Vancouver': 'PST8PDT,M3.2.0,M11.1.0',
  'Atlantic/Azores': '<-01>1<+00>,M3.5.0/0,M10.5.0/1',
  'Europe/London': 'GMT0BST,M3.5.0/1,M10.5.0',
  'Europe/Dublin': 'IST-1GMT0,M10.5.0,M3.5.0/1',
  'Europe/Lisbon': 'WET0WEST,M3.5.0/1,M10.5.0',
  'Europe/Paris': 'CET-1CEST,M3.5.0,M10.5.0/3',
  'Europe/Berlin': 'CET-1CEST,M3.5.0,M10.5.0/3',
  'Europe/Amsterdam': 'CET-1CEST,M3.5.0,M10.5.0/3',
  'Europe/Madrid': 'CET-1CEST,M3.5.0,M10.5.0/3',
  'Europe/Rome': 'CET-1CEST,M3.5.0,M10.5.0/3',
  'Europe/Athens': 'EET-2EEST,M3.5.0/3,M10.5.0/4',
  'Australia/Sydney': 'AEST-10AEDT,M10.1.0,M4.1.0/3',
  'Australia/Melbourne': 'AEST-10AEDT,M10.1.0,M4.1.0/3',
  'Pacific/Auckland': 'NZST-12NZDT,M9.5.0,M4.1.0/3',
};

function offsetMinutes(tz: string, ms: number): number | null {
  try {
    const p: Record<string, number> = {};
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    for (const x of f.formatToParts(new Date(ms))) if (x.type !== 'literal') p[x.type] = Number(x.value);
    return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ms / 60000) * 60000) / 60000);
  } catch {
    return null;
  }
}

export function posixTz(iana: string, now = Date.now()): string {
  if (POSIX[iana]) return POSIX[iana];
  const y = new Date(now).getUTCFullYear();
  const jan = offsetMinutes(iana, Date.UTC(y, 0, 15));
  const jul = offsetMinutes(iana, Date.UTC(y, 6, 15));
  if (jan === null || jul === null || jan !== jul) return ''; // 有 DST 又不在表裡：不帶 z
  if (jan === 0) return 'UTC0';
  const sign = jan > 0 ? '+' : '-';
  const a = Math.abs(jan);
  const hh = String(Math.floor(a / 60)).padStart(2, '0');
  const mm = a % 60 ? String(a % 60).padStart(2, '0') : '';
  // POSIX 的偏移號與 UTC 偏移相反：UTC+8 → <+08>-8
  return `<${sign}${hh}${mm}>${jan > 0 ? '-' : ''}${Math.floor(a / 60)}${mm ? ':' + mm : ''}`;
}
